import { useState, useEffect, useContext, useCallback } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useBilling = (userID, interval) => {
  const { baseUrl, basicAuth } = useContext(GlobalContext);

  const [walletBalance, setWalletBalance] = useState(0);
  const [billingData, setBillingData] = useState({});
  const [ledgerData, setLedgerData] = useState([]);
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(false);

  /* ================= DATE STATE ================= */

  const getCurrentDate = () =>
    new Date().toISOString().split("T")[0];

  const getOneDayBackDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split("T")[0];
  };

  const [startDateCurrent, setStartDateCurrent] =
    useState(getOneDayBackDate());

  const [endDateCurrent, setEndDateCurrent] =
    useState(getCurrentDate());

  /* ================= COMMON POST ================= */

  const postRequest = useCallback(
    async (endpoint, body) => {
      if (!baseUrl || !basicAuth) return null;

      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      return res.json();
    },
    [baseUrl, basicAuth]
  );

  /* ================= API CALLS ================= */

  const fetchWalletBalance = useCallback(async () => {
    if (!userID) return;

    try {
      const data = await postRequest(
        "/Whatsapp/v1.0/getParentWallet",
        { userid: userID, secret: "string" }
      );

      if (data?.responseCode === "00") {
        setWalletBalance(Number(data?.data?.wl_balance || 0));
      }
    } catch (err) {
      console.error("Wallet API Error:", err);
      setWalletBalance(0);
    }
  }, [userID, postRequest]);

  const fetchBillingData = useCallback(async () => {
    if (!userID) return;

    try {
      const data = await postRequest(
        "/Whatsapp/v1/getBillingData",
        { userid: userID, interval, secret: "string" }
      );

      if (data?.responseCode === "00") {
        setBillingData(data?.data || {});
      }
    } catch (err) {
      console.error("Billing API Error:", err);
      setBillingData({});
    }
  }, [userID, interval, postRequest]);

  const fetchLedgerData = useCallback(async () => {
    if (!userID) return;

    try {
      setLoading(true);

      const data = await postRequest(
        "/Whatsapp/getBillingLedger",
        {
          userid: userID,
          fromDate: startDateCurrent,
          toDate: endDateCurrent,
        }
      );

      if (data?.responseCode === "00") {
        setLedgerData(data?.data || []);
      } else {
        setLedgerData([]);
      }
    } catch (err) {
      console.error("Ledger API Error:", err);
      setLedgerData([]);
    } finally {
      setLoading(false);
    }
  }, [userID, startDateCurrent, endDateCurrent, postRequest]);

  const fetchInsights = useCallback(async () => {
    if (!userID) return;

    try {
      setLoading(true);

      const data = await postRequest(
        "/Whatsapp/whatsappInsights",
        { userid: userID, secret: "string" }
      );

      if (data?.responseCode === "00") {
        setInsights(data?.data || {});
      }
    } catch (err) {
      console.error("Insights API Error:", err);
      setInsights({});
    } finally {
      setLoading(false);
    }
  }, [userID, postRequest]);

  /* ================= EFFECTS ================= */

  useEffect(() => {
    if (!userID) return;
    fetchWalletBalance();
    fetchInsights();
  }, [userID, fetchWalletBalance, fetchInsights]);

  useEffect(() => {
    if (!userID) return;
    fetchBillingData();
  }, [userID, interval, fetchBillingData]);

  useEffect(() => {
    if (!userID) return;
    fetchLedgerData();
  }, [userID, startDateCurrent, endDateCurrent, fetchLedgerData]);

  return {
    walletBalance,
    billingData,
    ledgerData,
    insights,
    loading,

    startDateCurrent,
    endDateCurrent,
    setStartDateCurrent,
    setEndDateCurrent,

    fetchLedgerData, 
  };
};

export default useBilling;